import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress, Popper } from "@mui/material";
import { useAccountsByLedger } from "../UseDataReader/useAccountsByLedger";

const BottomStartPopper = (props) => {
  const anchorRect = props.anchorEl?.getBoundingClientRect?.();
  const viewportBottom = window.innerHeight || 0;
  const freeSpaceBelow = Math.floor(viewportBottom - (anchorRect?.bottom || 0) - 10);
  const adaptiveMaxHeight = Math.max(96, Math.min(220, freeSpaceBelow));

  return (
    <Popper
      {...props}
      placement="bottom-start"
      popperOptions={{ strategy: "fixed" }}
      modifiers={[
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
      ]}
      style={{
        ...props.style,
        "--accounts-list-max-height": `${adaptiveMaxHeight}px`,
      }}
    />
  );
};

export default function AccountsAutocompleteInsert({
  ledgerID,
  reloadKey,
  value,
  onChange,
  onSelect,
  onClear,
  label = "Kërko",
  labelId,
  textFieldWidth,
  marginFields,
  endAdornment,
  disableAutoFocus = false,
}) {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { listAccounts, loadingInitial, loadingMore, setPage, hasMore } =
    useAccountsByLedger({ ledgerID, searchTerm });

  // =========================
  // DEBOUNCE SEARCH
  // =========================
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearchTerm(inputValue);
      setPage(1);
    }, 300);

    return () => clearTimeout(delay);
  }, [inputValue]);

  // =========================
  // SCROLL PAGINATION
  // =========================
  const handleScroll = (event) => {
    const listbox = event.currentTarget;

    if (
      listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 5 &&
      hasMore &&
      !loadingMore
    ) {
      setPage((prev) => prev + 1);
    }
  };

  // =========================
  // CHANGE HANDLER
  // =========================
  const handleChange = (event, option) => {
    if (!option) {
      setInputValue("");
      onClear?.();
      return;
    }

    setInputValue(`${option.IdentificationNumber} - ${option.AccountName}`);
    onSelect?.(option);
  };

  useEffect(() => {
    if (!disableAutoFocus && !value && labelId) {
      setTimeout(() => {
        document.getElementById(labelId)?.focus();
      }, 0);
    }
  }, [value, labelId, disableAutoFocus]);
  useEffect(() => {
    if (!value) {
      setInputValue("");
      return;
    }

    const nextLabel = `${value.IdentificationNumber || ""} - ${value.AccountName || ""}`.trim();
    setInputValue(nextLabel === "-" ? "" : nextLabel);
  }, [value]);

  useEffect(() => {
    setSearchTerm("");
    setPage(1);
  }, [reloadKey]);

  return (
    <Autocomplete
      fullWidth
      PopperComponent={BottomStartPopper}
      options={listAccounts}
      id={labelId}
      value={value}
      inputValue={inputValue}
      loading={loadingInitial}
      noOptionsText="Nuk u gjet asnjë rezultat"
      loadingText="Duke kërkuar..."
      filterOptions={(x) => x}
      onChange={handleChange}
      onInputChange={(e, v, reason) => {
        if (reason === "input") setInputValue(v);
      }}
      onOpen={() => {
        if (value) {
          setSearchTerm("");
          setPage(1);
        }
      }}
      isOptionEqualToValue={(o, v) => o.AccountID === v?.AccountID}
      getOptionLabel={(o) =>
        o ? `${o.IdentificationNumber} - ${o.AccountName}` : ""
      }
      sx={{
        "& .MuiAutocomplete-paper": {
          marginTop: "6px",
          border: "1px solid #dce9e2",
          borderRadius: "12px",
          boxShadow: "0 10px 24px rgba(31, 58, 54, 0.12)",
        },
        "& .MuiAutocomplete-listbox": {
          padding: "6px",
        },
        "& .MuiAutocomplete-option.Mui-focused": {
          backgroundColor: "#e8f3ee !important",
        },
        "& .MuiAutocomplete-option[aria-selected='true']": {
          backgroundColor: "#dcefe7 !important",
        },
        "& .MuiAutocomplete-option.Mui-focused[aria-selected='true']": {
          backgroundColor: "#d4eadf !important",
        },
      }}
      ListboxProps={{
        style: {
          maxHeight: "var(--accounts-list-max-height, 220px)",
          overflow: "auto",
          paddingTop: 2,
          paddingBottom: 2,
        },
        onScroll: handleScroll,
      }}
      renderOption={(props, option, { index }) => {
        return (
          <li
            {...props}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #edf3ef",
              borderRadius: "8px",
              marginBottom: "2px",
              padding: "9px 10px",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#5f6f68", letterSpacing: "0.1px" }}>
                {option.IdentificationNumber || "Pa ID"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1f3a36" }}>
                {option.AccountName}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {loadingMore && index === listAccounts.length - 1 && (
                <CircularProgress size={14} />
              )}
              <span style={{ color: "#7a9388", fontSize: 14, fontWeight: 700 }}>›</span>
            </div>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#f7fbf9",
              minHeight: "50px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d4e2db",
            },
            "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#4E7B67",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#4E7B67",
              borderWidth: "2px",
            },
            "& .MuiInputLabel-root": {
              color: "#476057",
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
            },
          }}
          style={{
            width: textFieldWidth || "95%",
            marginLeft: marginFields ?? undefined,
          }}
        />
      )}
    />
  );
}
